// Authentication related functions will go here

import { supabase, callSupabaseFunction } from './client'
import { auth } from '@/utils/auth'

// ============================================================
// Authentication Functions
// ============================================================

/**
 * Authenticates a user using email and password
 * @param {string} email - User's email address
 * @param {string} password - User's password
 * @returns {Promise<Object>} Session data
 * @throws {Error} If authentication fails
 */
export const signInWithPassword = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  })

  if (error) throw error
  return data
}

/**
 * Initiates Google OAuth sign-in process
 * @returns {Promise<Object>} OAuth response data
 * @throws {Error} If the OAuth process fails
 */
export const signInWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`
    }
  })

  if (error) throw error
  return data
}

/**
 * Initiates LinkedIn OAuth sign-in process
 * @returns {Promise<Object>} OAuth response data
 * @throws {Error} If the OAuth process fails
 */
export const signInWithLinkedIn = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'linkedin_oidc',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`
    }
  })

  if (error) throw error
  return data
}

/**
 * Exchanges an OAuth code for a session
 * @param {string} code - OAuth code from the callback
 * @returns {Promise<Object>} Session data
 * @throws {Error} If the code exchange fails
 */
export const exchangeCodeForSession = async code => {
  const { data, error } = await supabase.auth.exchangeCodeForSession(code)

  if (error) throw error
  return data
}

/**
 * Signs up a new user with email and password
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @param {Object} metadata - Additional user metadata
 * @returns {Promise<Object>} Signup response data
 * @throws {Error} If signup fails
 */
export const signUp = async ({ email, password, first_name, last_name }) => {
  const userSignupResponse = await callSupabaseFunction({
    functionName: 'user-signup',
    body: {
      email,
      password,
      first_name,
      last_name
    },
    hasSession: false
  })
  return userSignupResponse
}

/**
 * Gets the current user's profile
 * @returns {Promise<Object|null>} Current user's profile data
 * @throws {Error} If the operation fails
 */
export const getCurrentProfile = async () => {
  const user = await auth.getUser()
  if (!user) return null

  const { data: profile, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', user.id)
    .single()

  if (error) throw error
  return profile
}

/**
 * Checks if the current user's profile has an organization with a customer
 * @returns {Promise<boolean>} True if the user's organization has a customer, false otherwise
 */
export const getCustomer = async () => {
  try {
    const profile = await getCurrentProfile()
    if (!profile?.organization_id) return null

    const { data: org, error } = await supabase
      .from('customers')
      .select('id')
      .eq('organization_id', profile.organization_id)
      .single()

    if (error) throw error

    return org.id
  } catch (error) {
    console.error('Error checking organization customer:', error)
    return null
  }
}

/**
 * Gets the current authenticated user
 * @returns {Promise<Object>} Current user data
 * @throws {Error} If the operation fails
 */
export const getCurrentUser = async () => {
  const user = await auth.getUser()
  if (!user) return null

  // Merge profile data with user metadata
  return {
    ...user,
    user_metadata: {
      ...user.user_metadata
    }
  }
}

/**
 * Sends a password reset email to the specified email address
 * @param {string} email - The email address to send the reset link to
 * @param {Object} options - Additional options for the reset email
 * @throws {Error} If sending the reset email fails
 */
export const sendPasswordResetEmail = async (email, options = {}) => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    ...options,
    redirectTo: `${window.location.origin}/reset-password`
  })
  if (error) throw error
}

/**
 * Updates the current user's password
 * @param {string} newPassword - The new password
 * @throws {Error} If the password update fails
 */
export const updatePassword = async newPassword => {
  const { error } = await supabase.auth.updateUser({
    password: newPassword
  })
  if (error) throw error
}

/**
 * Updates the current user's metadata
 * @param {Object} metadata - Object containing user_metadata updates
 * @param {Object} [appMetadata] - Optional object containing app_metadata updates
 * @returns {Promise<Object>} Updated user data
 * @throws {Error} If the update fails
 */
export const updateUserMetadata = async (metadata, appMetadata) => {
  const updateData = {
    data: metadata
  }

  // Only include app_metadata if it's provided
  if (appMetadata && Object.keys(appMetadata).length > 0) {
    updateData.app_metadata = appMetadata
  }

  const { data, error } = await supabase.auth.updateUser(updateData)
  if (error) throw error
  return data
}

/**
 * Sign out the current user
 * @returns {Promise<{ error: Error|null }>}
 */
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut({ scope: 'local' })
    if (error) throw error
    return { error: null }
  } catch (error) {
    console.error('Error signing out:', error)
    return { error }
  }
}

/**
 * Get the current session
 * @returns {Object|null} The current session or null if not authenticated
 */
export const getSession = () => {
  try {
    return supabase.auth.session
  } catch (error) {
    console.error('Error getting session:', error)
    return null
  }
}

/**
 * Set the current session
 * @param {Object} session - The session object to set
 */
export const setSession = session => {
  try {
    supabase.auth.setSession(session)
  } catch (error) {
    console.error('Error setting session:', error)
  }
}

/**
 * Subscribes to auth state changes
 * @param {Function} callback - Function to call when auth state changes
 * @returns {Function} Unsubscribe function
 */
export const onAuthStateChange = callback => {
  return supabase.auth.onAuthStateChange(callback)
}
